import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
  inject,
  signal,
  EventEmitter,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { HSStaticMethods } from 'preline/non-auto';
import { filter } from 'rxjs';

import { CourseEditorService } from 'core/services/course-editor.service';
import { ToastService } from 'core/services/toast.service';
import { ConfirmDialogService } from 'core/services/confirm-dialog.service';
import {
  Course,
  CourseNavigation,
  Lesson,
  Module,
  Question,
} from 'core/models/course-content.model';

import { CourseHeader } from 'components/course-header/course-header';
import { CourseSidebar } from 'components/course-sidebar/course-sidebar';
import { CourseInfo } from 'components/course-info/course-info';
import { ActionBar } from './shared/components/action-bar/action-bar';

import { VideoEditor } from './content/video-editor/video-editor';
import { VideoPreview } from './content/video-preview/video-preview';

import { PdfEditor } from './content/pdf-editor/pdf-editor';
import { PdfPreview } from './content/pdf-preview/pdf-preview';

import { EvaluationEditor } from './content/evaluation-editor/evaluation-editor';
import { EvaluationPreview } from './content/evaluation-preview/evaluation-preview';

import { QuestionPreview } from './content/question-preview/question-preview';

import { ModuleForm } from './shared/forms/module-form/module-form';
import { LessonForm } from './shared/forms/lesson-form/lesson-form';
import { QuestionForm } from './shared/forms/question-form/question-form';

@Component({
  imports: [
    CommonModule,
    CourseSidebar,
    CourseHeader,
    CourseInfo,
    ActionBar,
    ModuleForm,
    LessonForm,
    QuestionForm,
  ],
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.html',
})
export class App implements OnInit, AfterViewInit {
  protected readonly courseService = inject(CourseEditorService);
  private readonly toastService = inject(ToastService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('contentContainer', { read: ViewContainerRef })
  contentContainer!: ViewContainerRef;
  @ViewChild('modalContainer', { read: ViewContainerRef })
  modalContainer!: ViewContainerRef;

  protected title = 'course-editor';
  protected isSidebarOpen = false;
  protected course: Course | null = null;
  protected navigation: CourseNavigation | null = null;
  protected currentLesson: Lesson | null = null;
  protected currentModule: Module | null = null;
  protected isPreviewMode = signal<boolean>(false);
  protected hasUnsavedChanges = signal<boolean>(false);
  protected showModuleForm = signal<boolean>(false);
  protected showLessonForm = signal<boolean>(false);
  protected showQuestionForm = signal<boolean>(false);
  protected selectedModuleId = signal<string | null>(null);
  protected editingModuleId = signal<string | null>(null);
  protected editingModuleName = signal<string>('');
  protected editingModuleDescription = signal<string>('');
  protected editingModuleContentType = signal<'lessons' | 'evaluations' | null>(
    null,
  );
  protected editingQuestionId = signal<string | null>(null);

  private contentComponentRef: ComponentRef<
    | { lesson?: Lesson; deleteLesson?: EventEmitter<void> }
    | {
        question?: Question;
        module?: Module;
        editQuestionRequest?: EventEmitter<Question>;
      }
    | { question: Question; moduleName?: string }
  > | null = null;
  private currentLoadedLessonId: string | null = null;
  private isLoadingComponent = false;
  private isChangingPreviewMode = false;

  ngOnInit(): void {
    this.isSidebarOpen = this.shouldSidebarStartOpen();

    // Sempre iniciar em modo editor (não preview)
    // O preview só será ativado quando o usuário clicar no botão
    this.isPreviewMode.set(false);
    this.courseService.setPreviewMode(false);

    // Carregar dados do curso (incluindo módulos e lições mockados se não houver dados salvos)
    this.loadCourseData();

    // Garantir que a primeira lição seja selecionada se não houver lição atual
    const navigation = this.courseService.getNavigation();
    if (
      !navigation.currentLesson &&
      this.course &&
      this.course.modules.length > 0
    ) {
      const firstModule = this.course.modules[0];
      if (
        firstModule.contentType === 'lessons' &&
        firstModule.lessons &&
        firstModule.lessons.length > 0
      ) {
        this.courseService.goToLesson(firstModule.lessons[0].id);
        this.loadCourseData();
      } else if (firstModule.contentType === 'evaluations') {
        // Para módulos evaluation, apenas definir como módulo atual
        this.courseService.setCurrentModule(firstModule.id);
        this.loadCourseData();
      }
    }

    this.courseService.lessonChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lessonId: string | null) => {
        if (this.currentLoadedLessonId !== lessonId) {
          // Limpar componente anterior quando a lição mudar
          this.clearContentContainer();
          this.loadCourseData();
        } else {
          this.updateCourseData();
        }
      });

    this.courseService.course$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.hasUnsavedChanges.set(true);
        this.updateCourseData();
      });

    this.courseService.previewMode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isPreview) => {
        const previousMode = this.isPreviewMode();

        // Se o modo não mudou, não fazer nada
        if (previousMode === isPreview) {
          return;
        }

        this.isChangingPreviewMode = true;
        this.isPreviewMode.set(isPreview);

        // Quando o modo preview mudar, destruir componente anterior e carregar o novo
        // Garantir que apenas um modo seja carregado por vez
        const currentQuestion = this.courseService.getCurrentQuestion();
        if (this.contentContainer && !this.isLoadingComponent) {
          // Limpar componente anterior completamente antes de carregar novo
          this.clearContentContainer();

          // Aguardar um ciclo para garantir que a limpeza foi concluída
          setTimeout(() => {
            // Verificar novamente o modo atual antes de carregar (pode ter mudado)
            // E garantir que não está em processo de loading
            if (
              this.isPreviewMode() === isPreview &&
              !this.isLoadingComponent
            ) {
              // Carregar componente baseado no novo modo e tipo de conteúdo
              if (
                this.currentModule?.contentType === 'evaluations' &&
                currentQuestion
              ) {
                // Para módulos de avaliação com questão selecionada
                if (isPreview) {
                  this.loadQuestionPreview();
                } else {
                  this.loadEvaluationEditor();
                }
              } else if (this.currentLesson) {
                // Para módulos de lições
                if (isPreview) {
                  this.loadPreviewComponent();
                } else {
                  this.loadEditorComponent();
                }
              }
            }
            this.isChangingPreviewMode = false;
          }, 0);
        } else {
          this.isChangingPreviewMode = false;
        }
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    this.initializePreline();
    this.cdr.markForCheck();

    // Garantir que os dados estejam atualizados após a view estar pronta
    this.updateCourseData();

    // Sempre carregar o editor por padrão, não o preview
    // Se não houver lição selecionada mas houver módulos/lições, selecionar a primeira
    if (!this.currentLesson && this.course && this.course.modules.length > 0) {
      const firstModule = this.course.modules[0];
      if (
        firstModule.contentType === 'lessons' &&
        firstModule.lessons &&
        firstModule.lessons.length > 0
      ) {
        this.courseService.goToLesson(firstModule.lessons[0].id);
        this.updateCourseData();
      } else if (firstModule.contentType === 'evaluations') {
        // Para módulos evaluation, apenas definir como módulo atual
        this.courseService.setCurrentModule(firstModule.id);
        this.updateCourseData();
      }
    }

    // Carregar componente se houver lição ou questão atual
    const currentQuestion = this.courseService.getCurrentQuestion();
    if (!this.contentComponentRef && this.contentContainer) {
      if (
        this.currentModule?.contentType === 'evaluations' &&
        currentQuestion
      ) {
        if (this.isPreviewMode()) {
          this.loadQuestionPreview();
        } else {
          this.loadEvaluationEditor();
        }
      } else if (this.currentLesson) {
        if (this.isPreviewMode()) {
          this.loadPreviewComponent();
        } else {
          this.loadEditorComponent();
        }
      }
    }

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.initializePreline());
  }

  protected onToggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
    if (!this.isSidebarOpen) {
      this.releaseSidebarFocus();
    }
  }

  protected onCloseSidebar(): void {
    this.isSidebarOpen = false;
    this.releaseSidebarFocus();
  }

  protected onTogglePreview(): void {
    const newMode = !this.isPreviewMode();
    this.courseService.setPreviewMode(newMode);
    // O componente será carregado pela subscription do previewMode$
  }

  protected onGoBack(): void {
    if (this.hasUnsavedChanges()) {
      if (confirm('Você tem alterações não salvas. Deseja realmente sair?')) {
        window.history.back();
      }
    } else {
      window.history.back();
    }
  }

  protected onModuleClick(moduleId: string): void {
    // Método chamado quando um módulo é clicado
    // Se o módulo está vazio, o service já foi atualizado no sidebar
    // Precisamos limpar o componente atual e atualizar a view para exibir a mensagem
    const module = this.courseService
      .getCourse()
      .modules.find((m) => m.id === moduleId);
    if (!module) {
      return;
    }

    if (
      module.contentType === 'lessons' &&
      module.lessons &&
      module.lessons.length > 0
    ) {
      // Se for um módulo de lições, navegar para a primeira lição
      const sortedLessons = [...module.lessons].sort(
        (a, b) => a.order - b.order,
      );
      if (sortedLessons[0]) {
        this.courseService.goToLesson(sortedLessons[0].id);
        this.updateCourseData();
        this.loadCourseData();
        return;
      }
    } else if (
      module.contentType === 'evaluations' &&
      module.questions &&
      module.questions.length > 0
    ) {
      // Se for um módulo de avaliações com questões, navegar para a primeira questão
      const sortedQuestions = [...module.questions].sort(
        (a, b) => a.order - b.order,
      );
      if (sortedQuestions[0]) {
        this.courseService.goToQuestion(sortedQuestions[0].id);
        this.updateCourseData();
        this.loadCourseData();
        return;
      }
    }

    // Caso contrário, apenas definir o módulo atual (módulo vazio)
    this.courseService.setCurrentModule(moduleId);
    if (this.contentContainer) {
      this.clearContentContainer();
    }
    this.loadCourseData();
  }

  protected onLessonClick(lessonId: string): void {
    if (this.currentLoadedLessonId === lessonId) {
      return;
    }
    // Navegar para a lição mantendo o estado atual de preview
    this.courseService.goToLesson(lessonId);
    this.updateCourseData();
    // Garantir que o conteúdo seja carregado
    this.loadCourseData();
  }

  protected onQuestionClick(questionId: string): void {
    // Navegar para a questão mantendo o estado atual de preview
    this.courseService.goToQuestion(questionId);
    this.updateCourseData();
    // Garantir que o conteúdo seja carregado
    this.loadCourseData();
  }

  protected onAddModule(): void {
    this.selectedModuleId.set(null);
    // Limpar estado de edição ao criar novo módulo
    this.editingModuleId.set(null);
    this.editingModuleName.set('');
    this.editingModuleDescription.set('');
    this.editingModuleContentType.set(null);
    this.showModuleForm.set(true);
  }

  protected onAddLesson(moduleId: string): void {
    this.selectedModuleId.set(moduleId);
    this.showLessonForm.set(true);
  }

  protected onAddQuestion(moduleId: string): void {
    this.selectedModuleId.set(moduleId);
    this.editingQuestionId.set(null);
    this.showQuestionForm.set(true);
  }

  protected onEditModule(moduleId: string): void {
    const module = this.course?.modules.find((m) => m.id === moduleId);
    if (module) {
      this.editingModuleId.set(moduleId);
      this.editingModuleName.set(module.name);
      this.editingModuleDescription.set(module.description);
      this.editingModuleContentType.set(module.contentType);
      this.showModuleForm.set(true);
    }
  }

  protected onEditLesson(data: { moduleId: string; lessonId: string }): void {
    // Garantir que estamos no modo de edição (não preview)
    if (this.isPreviewMode()) {
      this.isPreviewMode.set(false);
      this.courseService.setPreviewMode(false);
    }
    // Navegar para a lição (se já não estiver selecionada)
    if (this.currentLesson?.id !== data.lessonId) {
      this.courseService.goToLesson(data.lessonId);
      this.loadCourseData();
    }
  }

  protected async onDeleteModule(moduleId: string): Promise<void> {
    const module = this.course?.modules.find((m) => m.id === moduleId);
    const confirmed = await this.confirmDialogService.confirm({
      title: 'Excluir Módulo',
      message: `Tem certeza que deseja excluir o módulo "${
        module?.name || 'este módulo'
      }"? Todas as lições serão excluídas.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (confirmed) {
      this.courseService.deleteModule(moduleId);
      this.toastService.success('Módulo excluído com sucesso!');
      this.loadCourseData();
    }
  }

  protected async onDeleteLesson(data: {
    moduleId: string;
    lessonId: string;
  }): Promise<void> {
    if (!this.course || !this.course.modules) {
      return;
    }
    const module = this.course.modules.find(
      (m) => m.id === data.moduleId && m.contentType === 'lessons' && m.lessons,
    );
    if (!module || !module.lessons) {
      return;
    }
    const lesson = module.lessons.find((l) => l.id === data.lessonId);

    const confirmed = await this.confirmDialogService.confirm({
      title: 'Excluir Lição',
      message: `Tem certeza que deseja excluir a lição "${
        lesson?.title || 'esta lição'
      }"?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.courseService.deleteLesson(data.moduleId, data.lessonId);
    this.toastService.success('Lição excluída com sucesso!');

    // Limpar componente atual após exclusão
    if (this.contentContainer) {
      this.clearContentContainer();
    }

    // Atualizar dados do curso
    this.loadCourseData();

    // Se o módulo ficou vazio após exclusão, definir como módulo atual
    const updatedModule = this.courseService
      .getCourse()
      .modules.find((m) => m.id === data.moduleId);
    if (updatedModule && updatedModule.contentType === 'evaluations') {
      this.courseService.setCurrentModule(data.moduleId);
      this.loadCourseData();
    } else if (
      updatedModule &&
      updatedModule.contentType === 'lessons' &&
      (!updatedModule.lessons || updatedModule.lessons.length === 0)
    ) {
      this.courseService.setCurrentModule(data.moduleId);
      this.loadCourseData();
    } else if (
      updatedModule &&
      updatedModule.contentType === 'lessons' &&
      updatedModule.lessons &&
      updatedModule.lessons.length > 0
    ) {
      // Se ainda há lições, navegar para a primeira
      const sortedLessons = [...updatedModule.lessons].sort(
        (a, b) => a.order - b.order,
      );
      if (sortedLessons[0]) {
        this.courseService.goToLesson(sortedLessons[0].id);
        this.loadCourseData();
      }
    }
  }

  protected onModuleFormSave(data: {
    name: string;
    description: string;
    contentType: 'lessons' | 'evaluations';
  }): void {
    const editingModuleId = this.editingModuleId();

    if (editingModuleId) {
      // Editar módulo existente (sem alterar contentType)
      this.courseService.updateModule(editingModuleId, {
        name: data.name,
        description: data.description,
      });
      this.toastService.success('Módulo atualizado com sucesso!');
    } else {
      // Criar novo módulo
      this.courseService.createModule({
        name: data.name,
        description: data.description,
        contentType: data.contentType,
      });
      this.toastService.success('Módulo criado com sucesso!');
    }

    // Limpar estado de edição
    this.editingModuleId.set(null);
    this.editingModuleName.set('');
    this.editingModuleDescription.set('');
    this.editingModuleContentType.set(null);
    this.showModuleForm.set(false);
    this.loadCourseData();
  }

  protected onModuleFormCancel(): void {
    // Limpar estado de edição
    this.editingModuleId.set(null);
    this.editingModuleName.set('');
    this.editingModuleDescription.set('');
    this.editingModuleContentType.set(null);
    this.showModuleForm.set(false);
  }

  protected onLessonFormSave(data: {
    title: string;
    description: string;
    type: Lesson['type'];
    duration: number;
    videoUrl?: string;
    pdfUrl?: string;
  }): void {
    const moduleId = this.selectedModuleId();
    if (moduleId) {
      const newLesson = this.courseService.createLesson(moduleId, {
        title: data.title,
        description: data.description,
        type: data.type,
        duration: data.duration,
        videoUrl: data.videoUrl,
        pdfUrl: data.pdfUrl,
      });

      this.showLessonForm.set(false);
      this.selectedModuleId.set(null);

      if (newLesson) {
        this.toastService.success('Lição criada com sucesso!');
        // Navegar automaticamente para a lição recém-criada
        this.courseService.goToLesson(newLesson.id);
        // Aguardar um ciclo do Angular para garantir que o estado seja atualizado
        // e que o courseSubject tenha emitido o novo estado
        requestAnimationFrame(() => {
          // Atualizar dados do curso após navegação
          this.updateCourseData();
          // Carregar o componente da lição
          this.loadCourseData();
        });
      } else {
        this.toastService.error('Erro ao criar lição. Tente novamente.');
        this.loadCourseData();
      }
    }
  }

  protected onLessonFormCancel(): void {
    this.showLessonForm.set(false);
    this.selectedModuleId.set(null);
  }

  protected onQuestionFormSave(question: Question): void {
    const moduleId = this.selectedModuleId();
    if (!moduleId) {
      return;
    }

    if (this.editingQuestionId()) {
      // Editar questão existente
      const updated = this.courseService.updateQuestion(moduleId, question.id, {
        text: question.text,
        type: question.type,
        points: question.points,
        options: question.options.map((opt) => ({
          label: opt.label,
          text: opt.text,
        })),
        correctAnswers: question.correctAnswers,
      });

      if (updated) {
        this.toastService.success('Questão atualizada com sucesso!');
      } else {
        this.toastService.error('Erro ao atualizar questão. Tente novamente.');
      }
    } else {
      // Criar nova questão
      const created = this.courseService.createQuestion(moduleId, {
        text: question.text,
        type: question.type,
        points: question.points,
        options: question.options.map((opt) => ({
          label: opt.label,
          text: opt.text,
        })),
        correctAnswers: question.correctAnswers,
      });

      if (created) {
        this.toastService.success('Questão criada com sucesso!');
      } else {
        this.toastService.error('Erro ao criar questão. Tente novamente.');
      }
    }

    this.showQuestionForm.set(false);
    this.selectedModuleId.set(null);
    this.editingQuestionId.set(null);
    this.loadCourseData();
  }

  protected onQuestionFormCancel(): void {
    this.showQuestionForm.set(false);
    this.selectedModuleId.set(null);
    this.editingQuestionId.set(null);
  }

  /**
   * Obtém a questão sendo editada para passar ao formulário
   */
  protected getEditingQuestion(): Question | null {
    if (!this.editingQuestionId() || !this.currentModule?.questions) {
      return null;
    }
    return (
      this.currentModule.questions.find(
        (q) => q.id === this.editingQuestionId(),
      ) ?? null
    );
  }

  private loadCourseData(): void {
    this.course = this.courseService.getCourse();
    this.navigation = this.courseService.getNavigation();
    this.currentLesson = this.navigation.currentLesson;
    this.currentModule = this.navigation.currentModule;

    // Se estamos mudando o preview mode, não interferir - deixar a subscription gerenciar
    if (this.isChangingPreviewMode) {
      this.cdr.markForCheck();
      return;
    }

    // Sempre limpar componente anterior antes de decidir o que carregar
    if (this.contentContainer) {
      this.clearContentContainer();
    }

    // Carregar componente baseado no modo atual (editor ou preview)
    // Para módulos do tipo evaluation, carregar EvaluationEditor ou QuestionPreview
    // Para módulos do tipo lessons, carregar componente da lição atual
    if (this.contentContainer && !this.isLoadingComponent) {
      const currentQuestion = this.courseService.getCurrentQuestion();
      if (
        this.currentModule?.contentType === 'evaluations' &&
        currentQuestion
      ) {
        // Carregar preview ou editor de questão para módulos do tipo evaluation com questão selecionada
        if (this.isPreviewMode()) {
          this.loadQuestionPreview();
        } else {
          this.loadEvaluationEditor();
        }
      } else if (
        this.currentModule?.contentType === 'evaluations' &&
        !currentQuestion
      ) {
        // Se for módulo evaluation mas não há questão selecionada, apenas garantir que o container está limpo
        this.cdr.markForCheck();
      } else if (this.currentLesson) {
        // Carregar componente da lição para módulos do tipo lessons
        if (this.isPreviewMode()) {
          this.loadPreviewComponent();
        } else {
          this.loadEditorComponent();
        }
      } else {
        // Se não houver lição ou módulo evaluation, apenas garantir que o container está limpo
        // A mensagem será exibida pelo template baseado em currentModule e isCurrentModuleEmpty
        this.cdr.markForCheck();
      }
    } else {
      this.cdr.markForCheck();
    }
  }

  /**
   * Verifica se o módulo atual está vazio (sem lições ou questões)
   */
  protected get isCurrentModuleEmpty(): boolean {
    if (!this.currentModule) {
      return false;
    }
    if (this.currentModule.contentType === 'lessons') {
      return (
        !this.currentModule.lessons || this.currentModule.lessons.length === 0
      );
    } else if (this.currentModule.contentType === 'evaluations') {
      return (
        !this.currentModule.questions ||
        this.currentModule.questions.length === 0
      );
    }
    return true;
  }

  protected updateCourseData(): void {
    this.course = this.courseService.getCourse();
    this.navigation = this.courseService.getNavigation();
    this.currentLesson = this.navigation.currentLesson;
    this.currentModule = this.navigation.currentModule;
  }

  private async loadEditorComponent(): Promise<void> {
    if (!this.currentLesson || !this.contentContainer) {
      return;
    }

    // Só carregar editor se não estiver em preview mode
    if (this.isPreviewMode()) {
      return;
    }

    // Se já está carregado o editor correto, não precisa recarregar
    if (
      this.contentComponentRef &&
      this.currentLoadedLessonId === this.currentLesson.id &&
      !this.isPreviewMode()
    ) {
      return;
    }

    if (this.isLoadingComponent) {
      return;
    }

    this.isLoadingComponent = true;

    try {
      // Sempre limpar completamente antes de carregar novo
      this.clearContentContainer();

      // Aguardar um ciclo para garantir que a limpeza foi concluída
      await new Promise((resolve) => setTimeout(resolve, 0));

      // Verificar novamente se ainda não está em preview mode
      if (this.isPreviewMode()) {
        return;
      }

      // Verificar se o container está realmente vazio antes de criar
      if (this.contentContainer.length > 0) {
        this.contentContainer.clear();
        // Aguardar mais um ciclo após limpar
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      const componentType = await this.getEditorComponent(
        this.currentLesson.type,
      );

      if (componentType && !this.isPreviewMode()) {
        // Verificar novamente se o container está vazio
        if (this.contentContainer.length > 0) {
          this.contentContainer.clear();
        }

        try {
          this.contentComponentRef =
            this.contentContainer.createComponent(componentType);
          this.contentComponentRef.setInput('lesson', this.currentLesson);
          this.currentLoadedLessonId = this.currentLesson.id;

          // Escutar evento de exclusão de lição
          const instance = this.contentComponentRef.instance;
          if (
            instance &&
            'deleteLesson' in instance &&
            instance.deleteLesson instanceof EventEmitter
          ) {
            instance.deleteLesson.subscribe(() => {
              if (this.currentModule && this.currentLesson) {
                this.onDeleteLesson({
                  moduleId: this.currentModule.id,
                  lessonId: this.currentLesson.id,
                });
              }
            });
          }

          this.contentComponentRef.changeDetectorRef.markForCheck();
        } catch (error) {
          // Se houver erro ao criar o componente, limpar
          console.error('Erro ao criar componente de editor:', error);
          this.clearContentContainer();
        }
      }
    } finally {
      this.isLoadingComponent = false;
    }
  }

  private async loadEvaluationEditor(): Promise<void> {
    if (
      !this.currentModule ||
      !this.contentContainer ||
      this.currentModule.contentType !== 'evaluations'
    ) {
      return;
    }

    // Só carregar editor se não estiver em preview mode
    if (this.isPreviewMode()) {
      return;
    }

    if (this.isLoadingComponent) {
      return;
    }

    this.isLoadingComponent = true;

    try {
      const currentQuestion = this.courseService.getCurrentQuestion();

      // Se já existe um componente e é o mesmo tipo, apenas atualizar o input
      if (
        this.contentComponentRef &&
        this.contentComponentRef.instance instanceof EvaluationEditor
      ) {
        this.contentComponentRef.setInput('question', currentQuestion);
        this.contentComponentRef.setInput('module', this.currentModule);
        this.contentComponentRef.changeDetectorRef.markForCheck();
        this.isLoadingComponent = false;
        return;
      }

      // Se não existe ou é de tipo diferente, limpar e criar novo
      this.clearContentContainer();

      // Aguardar um ciclo para garantir que a limpeza foi concluída
      await new Promise((resolve) => setTimeout(resolve, 0));

      // Verificar novamente se ainda não está em preview mode
      if (this.isPreviewMode()) {
        return;
      }

      // Verificar se o container está realmente vazio antes de criar
      if (this.contentContainer.length > 0) {
        this.contentContainer.clear();
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      try {
        this.contentComponentRef =
          this.contentContainer.createComponent(EvaluationEditor);
        this.contentComponentRef.setInput('module', this.currentModule);
        this.contentComponentRef.setInput('question', currentQuestion);
        this.currentLoadedLessonId = null; // Não há lição para módulos evaluation

        // Escutar evento de edição de questão
        const instance = this.contentComponentRef.instance;
        if (
          instance &&
          'editQuestionRequest' in instance &&
          instance.editQuestionRequest instanceof EventEmitter
        ) {
          instance.editQuestionRequest.subscribe((question: Question) => {
            this.editingQuestionId.set(question.id);
            this.selectedModuleId.set(this.currentModule?.id || null);
            this.showQuestionForm.set(true);
          });
        }
      } catch (error) {
        console.error(
          'Erro ao criar componente de editor de avaliação:',
          error,
        );
        this.clearContentContainer();
      }
    } finally {
      this.isLoadingComponent = false;
    }
  }

  private async loadQuestionPreview(): Promise<void> {
    if (
      !this.currentModule ||
      !this.contentContainer ||
      this.currentModule.contentType !== 'evaluations'
    ) {
      return;
    }

    // Só carregar preview se estiver em preview mode
    if (!this.isPreviewMode()) {
      return;
    }

    const currentQuestion = this.courseService.getCurrentQuestion();
    if (!currentQuestion) {
      return;
    }

    // Se já está carregado o preview correto, não precisa recarregar
    if (
      this.contentComponentRef &&
      this.contentComponentRef.instance instanceof QuestionPreview
    ) {
      const instance = this.contentComponentRef.instance as QuestionPreview;
      if (
        instance.question?.id === currentQuestion.id &&
        this.isPreviewMode()
      ) {
        // Atualizar inputs se necessário
        this.contentComponentRef.setInput('question', currentQuestion);
        this.contentComponentRef.setInput(
          'moduleName',
          this.currentModule.name,
        );
        this.contentComponentRef.changeDetectorRef.markForCheck();
        return;
      }
    }

    if (this.isLoadingComponent) {
      return;
    }

    this.isLoadingComponent = true;

    try {
      // Sempre limpar completamente antes de carregar novo
      this.clearContentContainer();

      // Aguardar um ciclo para garantir que a limpeza foi concluída
      await new Promise((resolve) => setTimeout(resolve, 0));

      // Verificar novamente se ainda está em preview mode
      if (!this.isPreviewMode()) {
        return;
      }

      // Verificar se o container está realmente vazio antes de criar
      if (this.contentContainer.length > 0) {
        this.contentContainer.clear();
        // Aguardar mais um ciclo após limpar
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      // Verificar novamente se currentQuestion ainda existe
      const updatedQuestion = this.courseService.getCurrentQuestion();
      if (!updatedQuestion) {
        this.isLoadingComponent = false;
        return;
      }

      if (this.isPreviewMode()) {
        // Verificar novamente se o container está vazio
        if (this.contentContainer.length > 0) {
          this.contentContainer.clear();
        }

        try {
          this.contentComponentRef =
            this.contentContainer.createComponent(QuestionPreview);
          this.contentComponentRef.setInput('question', updatedQuestion);
          this.contentComponentRef.setInput(
            'moduleName',
            this.currentModule.name,
          );
          this.currentLoadedLessonId = null; // Não há lição para módulos evaluation
          this.contentComponentRef.changeDetectorRef.markForCheck();
        } catch (error) {
          // Se houver erro ao criar o componente, limpar e tentar novamente
          console.error(
            'Erro ao criar componente de preview de questão:',
            error,
          );
          this.clearContentContainer();
        }
      }
    } finally {
      this.isLoadingComponent = false;
    }
  }

  private async loadPreviewComponent(): Promise<void> {
    if (!this.currentLesson || !this.contentContainer) {
      return;
    }

    // Só carregar preview se estiver em preview mode
    if (!this.isPreviewMode()) {
      return;
    }

    // Se já está carregado o preview correto, não precisa recarregar
    if (
      this.contentComponentRef &&
      this.currentLoadedLessonId === this.currentLesson.id &&
      this.isPreviewMode()
    ) {
      return;
    }

    if (this.isLoadingComponent) {
      return;
    }

    this.isLoadingComponent = true;

    try {
      // Sempre limpar completamente antes de carregar novo
      this.clearContentContainer();

      // Aguardar um ciclo para garantir que a limpeza foi concluída
      await new Promise((resolve) => setTimeout(resolve, 0));

      // Verificar novamente se ainda está em preview mode
      if (!this.isPreviewMode()) {
        return;
      }

      // Verificar se o container está realmente vazio antes de criar
      if (this.contentContainer.length > 0) {
        this.contentContainer.clear();
        // Aguardar mais um ciclo após limpar
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      // Verificar novamente se currentLesson ainda existe antes de acessar type
      if (!this.currentLesson) {
        this.isLoadingComponent = false;
        return;
      }

      const componentType = await this.getPreviewComponent(
        this.currentLesson.type,
      );

      if (componentType && this.isPreviewMode()) {
        // Verificar novamente se o container está vazio
        if (this.contentContainer.length > 0) {
          this.contentContainer.clear();
        }

        try {
          this.contentComponentRef =
            this.contentContainer.createComponent(componentType);
          this.contentComponentRef.setInput('lesson', this.currentLesson);
          this.currentLoadedLessonId = this.currentLesson.id;
          this.contentComponentRef.changeDetectorRef.markForCheck();
        } catch (error) {
          // Se houver erro ao criar o componente, limpar e tentar novamente
          console.error('Erro ao criar componente de preview:', error);
          this.clearContentContainer();
        }
      }
    } finally {
      this.isLoadingComponent = false;
    }
  }

  private async getPreviewComponent(
    type: Lesson['type'],
  ): Promise<{ new (...args: unknown[]): { lesson?: Lesson } } | null> {
    switch (type) {
      case 'video':
        return VideoPreview;
      case 'pdf':
        return PdfPreview;
      case 'evaluation':
        return EvaluationPreview;
      case 'evaluation-results':
        // TODO: Implementar quando componente for criado
        return null;
      default:
        return null;
    }
  }

  private clearContentContainer(): void {
    // Destruir componente se existir
    if (this.contentComponentRef) {
      try {
        this.contentComponentRef.destroy();
      } catch {
        // Ignorar erros ao destruir (pode já ter sido destruído)
      }
      this.contentComponentRef = null;
      this.currentLoadedLessonId = null;
    }
    // Limpar container
    if (this.contentContainer) {
      try {
        this.contentContainer.clear();
      } catch {
        // Ignorar erros ao limpar
      }
    }
  }

  private async getEditorComponent(
    type: Lesson['type'],
  ): Promise<{ new (...args: unknown[]): { lesson?: Lesson } } | null> {
    switch (type) {
      case 'video':
        return VideoEditor;
      case 'pdf':
        return PdfEditor;
      case 'evaluation':
        return EvaluationEditor;
      case 'evaluation-results':
        // TODO: Implementar quando componente for criado
        return null;
      default:
        return null;
    }
  }

  private shouldSidebarStartOpen(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  private releaseSidebarFocus(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const activeElement = document.activeElement;
    const sidebarElement = document.getElementById('course-editor-sidebar');

    if (
      activeElement instanceof HTMLElement &&
      sidebarElement?.contains(activeElement)
    ) {
      activeElement.blur();
    }
  }

  private initializePreline(): void {
    if (typeof window === 'undefined') {
      return;
    }

    requestAnimationFrame(() => {
      HSStaticMethods.cleanCollection();
      HSStaticMethods.autoInit();
      const overlay = (globalThis as { HSOverlay?: { autoInit: () => void } })
        .HSOverlay;
      overlay?.autoInit();
    });
  }
}
